/**
 * Firebase実装のエッジリポジトリ
 */

import { v4 as uuidv4 } from 'uuid';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { IEdgeRepository } from '@/repositories/interfaces';
import { TopicEdge } from '@/types/edgeTypes';
import { getFirestoreInstance } from '@/lib/firebase';
import { FirebaseMapRepository } from './FirebaseMapRepository';

/**
 * Firebase実装のエッジリポジトリクラス
 */
export class FirebaseEdgeRepository implements IEdgeRepository {
  private db = getFirestoreInstance();
  
  /**
   * コンストラクタ
   * @param mapRepository マップリポジトリの参照
   */
  constructor(private mapRepository: FirebaseMapRepository) {}

  /**
   * 指定IDのエッジを取得
   */
  public async getEdge(mapId: string, edgeId: string): Promise<TopicEdge | null> {
    try {
      // マップからエッジを取得
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return null;
      }

      const edge = map.edges.find(e => e.id === edgeId);
      return edge ? { ...edge } : null;
    } catch (error) {
      console.error('Error getting edge:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのエッジを取得
   */
  public async getUserProjectEdge(userId: string, projectId: string, edgeId: string): Promise<TopicEdge | null> {
    try {
      const edgeDoc = await getDoc(doc(this.db, `users/${userId}/projects/${projectId}/edges`, edgeId));
      
      if (edgeDoc.exists()) {
        return { ...edgeDoc.data(), id: edgeDoc.id } as TopicEdge;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user project edge:', error);
      throw error;
    }
  }

  /**
   * 指定マップの全エッジを取得
   */
  public async getEdges(mapId: string): Promise<TopicEdge[]> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return [];
      }

      return map.edges.map(edge => ({ ...edge }));
    } catch (error) {
      console.error('Error getting edges:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトの全エッジを取得
   */
  public async getUserProjectEdges(userId: string, projectId: string): Promise<TopicEdge[]> {
    try {
      const edgesSnapshot = await getDocs(collection(this.db, `users/${userId}/projects/${projectId}/edges`));
      
      return edgesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as TopicEdge));
    } catch (error) {
      console.error('Error getting user project edges:', error);
      throw error;
    }
  }

  /**
   * エッジを更新
   */
  public async updateEdge(
    mapId: string,
    edgeId: string,
    update: Partial<TopicEdge>
  ): Promise<TopicEdge | null> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return null;
      }

      const edgeIndex = map.edges.findIndex(e => e.id === edgeId);
      if (edgeIndex === -1) {
        return null;
      }

      const updatedEdge = {
        ...map.edges[edgeIndex],
        ...update
      };

      const updatedEdges = [
        ...map.edges.slice(0, edgeIndex),
        updatedEdge,
        ...map.edges.slice(edgeIndex + 1)
      ];

      await this.mapRepository.updateMap(mapId, { edges: updatedEdges });
      return { ...updatedEdge };
    } catch (error) {
      console.error('Error updating edge:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのエッジを更新
   */
  public async updateUserProjectEdge(
    userId: string,
    projectId: string,
    edgeId: string,
    update: Partial<TopicEdge>
  ): Promise<TopicEdge | null> {
    try {
      const edgeRef = doc(this.db, `users/${userId}/projects/${projectId}/edges`, edgeId);
      const edgeDoc = await getDoc(edgeRef);
      
      if (!edgeDoc.exists()) {
        return null;
      }
      
      const updatedEdge = {
        ...edgeDoc.data(),
        ...update
      };
      
      await updateDoc(edgeRef, updatedEdge);
      
      return { ...updatedEdge, id: edgeId } as TopicEdge;
    } catch (error) {
      console.error('Error updating user project edge:', error);
      throw error;
    }
  }

  /**
   * 新しいエッジを作成
   */
  public async createEdge(mapId: string, edge: Omit<TopicEdge, 'id'>): Promise<TopicEdge> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        throw new Error(`Map with ID ${mapId} not found`);
      }

      const newEdge: TopicEdge = {
        ...edge,
        id: uuidv4()
      };

      const updatedEdges = [...map.edges, newEdge];
      await this.mapRepository.updateMap(mapId, { edges: updatedEdges });

      return { ...newEdge };
    } catch (error) {
      console.error('Error creating edge:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトに新しいエッジを作成
   */
  public async createUserProjectEdge(
    userId: string,
    projectId: string,
    edge: Omit<TopicEdge, 'id'>
  ): Promise<TopicEdge> {
    try {
      const edgeId = uuidv4();
      const edgeRef = doc(this.db, `users/${userId}/projects/${projectId}/edges`, edgeId);
      
      await setDoc(edgeRef, {
        ...edge,
        id: edgeId
      });
      
      return { ...edge, id: edgeId } as TopicEdge;
    } catch (error) {
      console.error('Error creating user project edge:', error);
      throw error;
    }
  }

  /**
   * エッジを削除
   */
  public async deleteEdge(mapId: string, edgeId: string): Promise<boolean> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return false;
      }

      const edgeIndex = map.edges.findIndex(e => e.id === edgeId);
      if (edgeIndex === -1) {
        return false;
      }

      const updatedEdges = [
        ...map.edges.slice(0, edgeIndex),
        ...map.edges.slice(edgeIndex + 1)
      ];

      await this.mapRepository.updateMap(mapId, { edges: updatedEdges });
      return true;
    } catch (error) {
      console.error('Error deleting edge:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのエッジを削除
   */
  public async deleteUserProjectEdge(
    userId: string,
    projectId: string,
    edgeId: string
  ): Promise<boolean> {
    try {
      const edgeRef = doc(this.db, `users/${userId}/projects/${projectId}/edges`, edgeId);
      
      await deleteDoc(edgeRef);
      return true;
    } catch (error) {
      console.error('Error deleting user project edge:', error);
      throw error;
    }
  }

  /**
   * エッジのステータスを更新
   */
  public async updateEdgeStatus(
    mapId: string,
    edgeId: string,
    status: TopicEdge['status']
  ): Promise<boolean> {
    try {
      const edge = await this.getEdge(mapId, edgeId);
      if (!edge) {
        return false;
      }

      await this.updateEdge(mapId, edgeId, { status });
      return true;
    } catch (error) {
      console.error('Error updating edge status:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのエッジステータスを更新
   */
  public async updateUserProjectEdgeStatus(
    userId: string,
    projectId: string,
    edgeId: string,
    status: TopicEdge['status']
  ): Promise<boolean> {
    try {
      const edgeRef = doc(this.db, `users/${userId}/projects/${projectId}/edges`, edgeId);
      
      await updateDoc(edgeRef, { status });
      return true;
    } catch (error) {
      console.error('Error updating user project edge status:', error);
      throw error;
    }
  }

  /**
   * 複数のエッジを一括で更新（バッチ処理）
   */
  public async bulkUpdateUserProjectEdges(
    userId: string,
    projectId: string,
    updates: { id: string; update: Partial<TopicEdge> }[]
  ): Promise<boolean> {
    try {
      const batch = writeBatch(this.db);
      
      for (const { id, update } of updates) {
        const edgeRef = doc(this.db, `users/${userId}/projects/${projectId}/edges`, id);
        batch.update(edgeRef, update);
      }
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error bulk updating user project edges:', error);
      throw error;
    }
  }
}